import { css } from '@emotion/react';
import _ from 'lodash';

import { theme } from '@adflow/frontend/ui-theme';
import { isNotNullish } from '@adflow/shared/util-js';

import { HistoryEntryFieldLayout } from './HistoryEntryFieldLayout';
import { HistoryIconMinus } from './HistoryIconMinus';
import { HistoryIconPlus } from './HistoryIconPlus';

type ImageItem = {
	id?: number | string;
	name: string;
	url?: string;
	thumbnailUrl?: string | null;
} | null;

const styles = {
	area: css({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '0.5rem',
	}),
	imagesContainer: css({
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: '0.5rem',
	}),
	imageItem: css({
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '0.25rem',
		padding: '0.5rem',
		border: `1px solid ${theme.colors.gray[300]}`,
		borderRadius: '0.25rem',
		backgroundColor: theme.colors.background.paper,
		maxWidth: '120px',
	}),
	imageItemRemoved: css({
		opacity: 0.6,
		backgroundColor: theme.colors.background.tag.gray,
	}),
	imageItemAdded: css({
		backgroundColor: theme.colors.background.tag.blue,
	}),
	thumbnail: css({
		width: '60px',
		height: '60px',
		objectFit: 'cover',
		borderRadius: '0.125rem',
		border: `1px solid ${theme.colors.gray[200]}`,
	}),
	imageName: css({
		fontSize: '0.75rem',
		color: theme.colors.text.secondary,
		textAlign: 'center',
		wordBreak: 'break-word',
		lineHeight: 1.2,
	}),
	placeholderThumbnail: css({
		width: '60px',
		height: '60px',
		backgroundColor: theme.colors.gray[100],
		border: `1px solid ${theme.colors.gray[200]}`,
		borderRadius: '0.125rem',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: '0.625rem',
		color: theme.colors.text.secondary,
		textAlign: 'center',
	}),
};

function formatImagesToComponents(images: ImageItem[], added = true) {
	return (
		<div css={styles.imagesContainer}>
			{images.filter(isNotNullish).map((image) => (
				<div
					key={image.id || image.name}
					css={[
						styles.imageItem,
						added ? styles.imageItemAdded : styles.imageItemRemoved,
					]}
				>
					{image.thumbnailUrl || image.url ? (
						<img
							src={image.thumbnailUrl || image.url}
							alt={image.name}
							css={styles.thumbnail}
							onError={(e) => {
								// Fallback if image fails to load
								const target = e.target as HTMLImageElement;
								target.style.display = 'none';
								const placeholder = target.nextSibling as HTMLElement;
								if (placeholder) {
									placeholder.style.display = 'flex';
								}
							}}
						/>
					) : (
						<div css={styles.placeholderThumbnail}>No Image</div>
					)}
					<div css={styles.imageName}>{image.name}</div>
				</div>
			))}
		</div>
	);
}

interface HistoryEntryFieldImagesProps {
	oldImages: ImageItem[];
	newImages: ImageItem[];
	label: string;
}

export function HistoryEntryFieldImages({
	oldImages,
	newImages,
	label,
}: HistoryEntryFieldImagesProps) {
	if (oldImages.length === 0 && newImages.length === 0) {
		return null;
	}

	const removedImages = _.differenceBy(
		oldImages,
		newImages,
		(img) => img?.id
	).filter(isNotNullish);
	const addedImages = _.differenceBy(
		newImages,
		oldImages,
		(img) => img?.id
	).filter(isNotNullish);

	if (addedImages.length === 0 && removedImages.length === 0) {
		return null;
	}

	return (
		<HistoryEntryFieldLayout label={label}>
			{addedImages.length > 0 ? (
				<div css={styles.area}>
					<HistoryIconPlus />
					{formatImagesToComponents(addedImages, true)}
				</div>
			) : null}
			{removedImages.length > 0 ? (
				<div css={styles.area}>
					<HistoryIconMinus />
					{formatImagesToComponents(removedImages, false)}
				</div>
			) : null}
		</HistoryEntryFieldLayout>
	);
}
